import Icon from '@/components/icon';
import Text from '@/components/text';
import useThemeMediaQuery from '@/hooks/use-theme-media-query';
import { getBreakpoint } from '@/theme';
import { alpha, Box, Theme } from '@mui/material';
import classNames from 'classnames';
import { FC } from 'react';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles((theme: Theme) => ({
  quickFilterButton: {
    padding: '2px 13px 2px 13px',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    cursor: 'pointer',
    userSelect: 'none',
    position: 'relative',
    '& .label': {
      lineHeight: 1,
      whiteSpace: 'nowrap',
    },
    '& .enabled-icon': {
      position: 'absolute',
      top: 0,
      right: 2,
      zIndex: 1,
      opacity: 0,
      transform: 'translateX(4px)',
      '& svg': {
        color: 'inherit',
      },
    },
    '&.enabled': {
      transition:
        'padding 50ms ease, border-color 50ms ease, background-color 50ms ease, color 50ms ease',
      padding: '2px 18px 2px 8px',
      borderColor: alpha(theme.palette.link.main, 0.9),
      backgroundColor: alpha(theme.palette.link.main, 0.1),
      color:
        theme.palette.mode === 'dark'
          ? theme.palette.common.white
          : alpha(theme.palette.link.main, 0.9),
      '& .enabled-icon': {
        opacity: 1,
        transform: 'translateX(0)',
        transition: 'opacity 50ms ease, transform 50ms ease',
      },
    },
    '@media (hover: hover)': {
      '&:hover': {
        borderColor: alpha(theme.palette.link.main, 0.5),
        backgroundColor: alpha(theme.palette.link.main, 0.1),
        color:
          theme.palette.mode === 'dark'
            ? theme.palette.common.white
            : alpha(theme.palette.link.main, 0.9),
      },
    },
  },
  [getBreakpoint('sm')]: {
    quickFilterButton: {
      padding: '2px 8px',
    },
  },
}));

interface Props {
  label: string;
  enabled: boolean;
  onChange: (value: boolean) => void;
}

const QuickFilterButton: FC<Props> = ({ label, enabled, onChange }) => {
  const styles = useStyles();

  const { small } = useThemeMediaQuery();

  const handleChange = () => {
    onChange(!enabled);
  };

  const compClass = classNames(styles.quickFilterButton, {
    enabled,
  });

  return (
    <Box className={compClass} role="button" onClick={handleChange}>
      <Text size={small ? 'x-small' : 'small'} className="label">
        {label}
      </Text>
      <Icon name="check-circle" size="x-small" className="enabled-icon" />
    </Box>
  );
};

export default QuickFilterButton;
